import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Share2, Plus, CheckCircle, XCircle, Clock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { formatDate, getInitials } from '@/lib/utils';
import { useAuthStore, ROL } from '@/store/auth.store';
import { ReferirPacienteModal } from '@/components/red/ReferirPacienteModal';
import { getErrorMessage } from '@/lib/errors';

const ESTADO_LABEL: Record<string, string> = {
  pendiente: 'Pendiente',
  aceptado: 'Aceptado',
  rechazado: 'Rechazado',
  completado: 'Completado',
};

const ESTADO_CLS: Record<string, string> = {
  pendiente: 'bg-amber-50 text-amber-700 border-amber-200',
  aceptado: 'bg-green-50 text-green-700 border-green-200',
  rechazado: 'bg-red-50 text-red-600 border-red-200',
  completado: 'bg-gray-100 text-gray-600 border-gray-200',
};

export function ReferidosPage() {
  const qc = useQueryClient();
  const { usuario } = useAuthStore();
  const canCreate = usuario?.rolId === ROL.SUPER_ADMIN || usuario?.rolId === ROL.MEDICO || usuario?.rolId === ROL.MEDICO_ADMIN;

  const [referirAbierto, setReferirAbierto] = useState(false);

  const { data: referidos = [], isLoading } = useQuery({
    queryKey: ['referidos'],
    queryFn: () => api.get('/api/referidos').then(r => r.data),
  });

  const aceptarMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/api/referidos/${id}/aceptar`).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['referidos'] }); toast.success('Referido aceptado'); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al aceptar referido')),
  });

  const rechazarMutation = useMutation({
    mutationFn: (id: string) => api.patch(`/api/referidos/${id}/rechazar`).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['referidos'] }); toast.success('Referido rechazado'); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al rechazar referido')),
  });

  const pendientes = referidos.filter((r: any) => r.estado === 'pendiente');
  const historial = referidos.filter((r: any) => r.estado !== 'pendiente');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Referidos</h2>
          <p className="text-sm text-text-secondary mt-0.5">
            {referidos.length} referido{referidos.length !== 1 ? 's' : ''}
            {pendientes.length > 0 && ` · ${pendientes.length} pendiente${pendientes.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => setReferirAbierto(true)}
            className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            <Plus size={15} /> Nuevo referido
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-text-secondary">Cargando...</div>
      ) : referidos.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-2xl border border-gray-100">
          <Share2 size={48} className="mx-auto text-gray-200 mb-4" />
          <p className="font-medium text-text-primary">Sin referidos aún</p>
          <p className="text-sm text-text-secondary mt-1 mb-5">
            Refiere pacientes a otros médicos directamente desde aquí o desde una consulta
          </p>
          {canCreate && (
            <button
              onClick={() => setReferirAbierto(true)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
            >
              Crear primer referido
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Pendientes de respuesta */}
          {pendientes.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Clock size={15} className="text-amber-500" />
                <h3 className="text-sm font-semibold text-text-primary">Pendientes de respuesta</h3>
              </div>
              <div className="space-y-3">
                {pendientes.map((r: any) => (
                  <div key={r.id} className="bg-white rounded-2xl border border-amber-100 p-5 flex items-start justify-between gap-4">
                    {/* Info del referido */}
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {/* Paciente */}
                      <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 font-bold text-sm flex-shrink-0">
                        {getInitials(`${r.paciente?.nombre || ''} ${r.paciente?.apellidoPaterno || ''}`)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-text-primary text-sm">
                          {r.paciente?.nombre} {r.paciente?.apellidoPaterno}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-text-secondary">
                          <span>Dr. {r.medicoOrigen?.nombre}</span>
                          <ArrowRight size={11} />
                          <span className="text-primary-600 font-medium">Dr. {r.medicoDestino?.nombre}</span>
                        </div>
                        {r.motivo && (
                          <p className="text-xs text-text-secondary italic mt-1 line-clamp-2">"{r.motivo}"</p>
                        )}
                        <p className="text-xs text-text-secondary mt-1.5">
                          {formatDate(r.fecha || r.createdAt)}
                        </p>
                      </div>
                    </div>

                    {/* Acciones */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => aceptarMutation.mutate(r.id)}
                        disabled={aceptarMutation.isPending}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-medium hover:bg-green-100 transition-colors disabled:opacity-50"
                      >
                        <CheckCircle size={13} /> Aceptar
                      </button>
                      <button
                        onClick={() => rechazarMutation.mutate(r.id)}
                        disabled={rechazarMutation.isPending}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
                      >
                        <XCircle size={13} /> Rechazar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Historial */}
          {historial.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-text-primary mb-3">Historial</h3>
              <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-50">
                {historial.map((r: any) => (
                  <div key={r.id} className="flex items-center justify-between px-5 py-3.5 hover:bg-gray-50/50 transition-colors">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 font-bold text-xs flex-shrink-0">
                        {getInitials(`${r.paciente?.nombre || ''} ${r.paciente?.apellidoPaterno || ''}`)}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">
                          {r.paciente?.nombre} {r.paciente?.apellidoPaterno}
                        </p>
                        <div className="flex items-center gap-1.5 text-xs text-text-secondary">
                          <span>Dr. {r.medicoOrigen?.nombre}</span>
                          <ArrowRight size={10} />
                          <span>Dr. {r.medicoDestino?.nombre}</span>
                          <span className="text-gray-300">·</span>
                          <span>{formatDate(r.fecha || r.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium border flex-shrink-0 ${ESTADO_CLS[r.estado] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                      {ESTADO_LABEL[r.estado] || r.estado}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal referir */}
      {referirAbierto && (
        <ReferirPacienteModal onClose={() => setReferirAbierto(false)} />
      )}
    </div>
  );
}
